import { type FilterValue, ProductTypes } from '@dealerportal/common';
import { SlugEquals } from './SlugCompare';

type SEOFriendlyFilters = FilterValue & ('Brand' | 'Type' | 'Line');

/**
 * Parses a list of SEO-friendly filters and returns an object of them.
 * @param seoFriendlyFilters The list of SEO-friendly filters to parse.
 * @returns The parsed SEO-friendly filters.
 */
export function parseSeoFriendlyFilters(
  seoFriendlyFilters: string[]
): Partial<Record<SEOFriendlyFilters, string[]>> {
  // Initialize the parsed result object
  const parsed: Partial<Record<SEOFriendlyFilters, string[]>> = {};

  // If no filters are present, return an empty object
  if (seoFriendlyFilters.length === 0) return parsed;

  const [firstFilter, secondFilter, thirdFilter] = seoFriendlyFilters.map(
    (filter) => filter?.split(/,|%2C/)
  );

  // If the first filter matches a product type, assign it to 'Type'. Otherwise, assign it to 'Brand'.
  if (
    firstFilter.some((filterItem) =>
      ProductTypes.some((type) => SlugEquals(type, filterItem, { lower: true }))
    )
  ) {
    parsed['Type'] = firstFilter;
  } else {
    parsed['Brand'] = firstFilter;
  }

  // If there are no further filters, return the parsed object
  if (secondFilter == null) return parsed;

  // If 'Type' is already populated, assign the second filter to 'Brand'. Otherwise, assign it to 'Line' and return.
  if (parsed['Type'] != null) {
    parsed['Brand'] = secondFilter;
  } else {
    parsed['Line'] = secondFilter;
    return parsed;
  }

  // If there are no further filters, return the parsed object
  if (thirdFilter == null) return parsed;

  // If there's a third filter, assign it to 'Line'
  parsed['Line'] = thirdFilter;

  return parsed;
}

/**
 * Converts parsed SEO-friendly filters into a URL path.
 * @param parsedSeoFriendlyFilters The parsed SEO-friendly filters.
 * @returns The SEO-friendly URL path.
 */
export function createSEOFriendlyPath(
  parsedSeoFriendlyFilters: Partial<Record<SEOFriendlyFilters, string[]>>
): string[] {
  // Create a copy of the parsed filters
  const copy = { ...parsedSeoFriendlyFilters };

  // If 'Brand' does not exist, ensure 'Line' is also not present.
  if (!copy['Brand'] || !copy['Brand']?.every((b) => b)) {
    delete copy['Line'];
  }
  // Generate the path parts from the filters
  const pathParts = [copy.Type, copy.Brand, copy.Line]
    .map((filter) => filter?.join(','))
    .filter((part) => part);
  // Filter out any null parts and return the final path
  return (pathParts as string[]) ?? [];
}
